import { useEffect, useMemo, useRef, useState } from 'react';
// @ts-expect-error
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
// @ts-expect-error
import View from '@arcgis/core/views/MapView';
// @ts-expect-error
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { useDispatch, useSelector } from 'react-redux';

import type { AppDispatch } from 'state/store';

import {
  CRACKMAP_INITIAL_ZOOM,
  CRACKMAP_STREET_ZOOM_DURATION_MS,
  isCrackmapWfsPrefetchZoom,
  resolveCrackmapStreetLevelGate,
} from 'domain/surveyDataManager/crackmaps/crackmapCatalog';
import { shouldShowCrackmapMapLoadingOverlay } from 'domain/surveyDataManager/crackmaps/crackmapLoading';
import { createCrackmapGeoJsonLayerConfig } from 'domain/surveyDataManager/crackmaps/createCrackmapGeoJsonLayer';
import {
  buildCrackmapBboxCacheKey,
  buildCrackmapStreetLevelPrefetchExtent,
  fetchCrackmapWfsFeatures,
  getCrackmapWfsErrorMessage,
  findContainingCachedCrackmapFeatures,
  isMapExtentApproximatelyEqual,
  type MapExtentBbox,
} from 'domain/surveyDataManager/crackmaps/crackmapWfs';
import type { GeoJsonFeatureCollection } from 'domain/surveyDataManager/geojsonBase';
import { isDefectOverlayLayerKind } from 'domain/surveyDataManager/defectOverlays/overlayKinds';
import {
  formatDefectOverlayLoadFailure,
  formatDefectOverlayLoadingMessage,
  resolveDefectOverlayProfile,
} from 'domain/surveyDataManager/defectOverlays/overlayProfile';
import { resolveDefectOverviewGrid } from 'domain/surveyDataManager/defectOverlays/resolveOverviewGrid';
import { revokeGeoJsonLayerUrl } from 'domain/surveyDataManager/map/geoJsonBlobUrl';
import { getDashboardMapViewPadding } from 'domain/surveyDataManager/map/mapViewPadding';
import {
  normalizeActiveDefectMapLayerId,
  toActiveDefectMapLayerId,
} from 'domain/surveyDataManager/map/activeLayerMapId';
import type {
  ActiveDefectLayer,
  ActiveDefectLayerId,
} from 'state/surveyDataManager/activeLayersSlice';
import { selectActiveDefectLayers, selectPendingMapLayerIds } from 'state/surveyDataManager';
import { layerMapRenderSettled } from 'state/surveyDataManager/uiSlice';
import { roundSurveyWfsPointZoom } from 'utils/surveyWfsPointSize';

export { shouldShowCrackmapMapLoadingOverlay } from 'domain/surveyDataManager/crackmaps/crackmapLoading';

const EXTENT_DEBOUNCE_MS = 400;
//Cap paint waits so a stuck layerView cannot freeze pan updates indefinitely.
const LAYER_VIEW_SETTLE_TIMEOUT_MS = 2500;

export interface DefectOverlayVectorsMapLoadingState {
  readonly isFetching: boolean;
  readonly message: string | null;
  readonly hasError: boolean;
  readonly errorMessage: string | null;
}

interface CrackmapFetchLoadingControls {
  readonly begin: (message: string) => void;
  readonly end: () => void;
  readonly fail: (errorMessage: string) => void;
  readonly clearError: () => void;
}

function useCrackmapFetchLoadingState(): {
  readonly loadingState: DefectOverlayVectorsMapLoadingState;
  readonly controls: CrackmapFetchLoadingControls;
} {
  const loadingCountRef = useRef(0);
  const [loadingState, setLoadingState] = useState<DefectOverlayVectorsMapLoadingState>({
    isFetching: false,
    message: null,
    hasError: false,
    errorMessage: null,
  });

  const controlsRef = useRef<CrackmapFetchLoadingControls>({
    begin: (message: string) => {
      loadingCountRef.current += 1;
      setLoadingState({
        isFetching: true,
        message,
        hasError: false,
        errorMessage: null,
      });
    },
    end: () => {
      loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
      if (loadingCountRef.current === 0) {
        setLoadingState((prev) => ({
          ...prev,
          isFetching: false,
          message: null,
        }));
      }
    },
    fail: (errorMessage: string) => {
      loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
      setLoadingState((prev) => ({
        isFetching: loadingCountRef.current > 0,
        message: loadingCountRef.current > 0 ? prev.message : null,
        hasError: true,
        errorMessage,
      }));
    },
    clearError: () => {
      setLoadingState((prev) =>
        prev.hasError
          ? { ...prev, hasError: false, errorMessage: null }
          : prev,
      );
    },
  });

  return { loadingState, controls: controlsRef.current };
}

interface TrackedCrackmapLayer {
  layer: InstanceType<typeof GeoJSONLayer> | null;
  blobUrl: string | null;
  featureCache: Map<string, GeoJsonFeatureCollection>;
  extentByCacheKey: Map<string, MapExtentBbox>;
  lastCacheKey: string | null;
  abortController: AbortController | null;
  refreshGeneration: number;
  hasSettledRender: boolean;
  replacePromise: Promise<void>;
}

interface CrackmapSessionCache {
  featureCache: Map<string, GeoJsonFeatureCollection>;
  extentByCacheKey: Map<string, MapExtentBbox>;
  lastCacheKey: string | null;
  hasSettledRender: boolean;
}

function cloneFeatureCache(
  source: ReadonlyMap<string, GeoJsonFeatureCollection>,
): Map<string, GeoJsonFeatureCollection> {
  return new Map(source);
}

function cloneExtentCache(
  source: ReadonlyMap<string, MapExtentBbox>,
): Map<string, MapExtentBbox> {
  return new Map(source);
}

function persistCrackmapSessionCache(
  layerId: ActiveDefectLayerId,
  tracked: TrackedCrackmapLayer,
  sessionCache: Map<ActiveDefectLayerId, CrackmapSessionCache>,
): void {
  if (tracked.featureCache.size === 0) {
    return;
  }

  sessionCache.set(layerId, {
    featureCache: cloneFeatureCache(tracked.featureCache),
    extentByCacheKey: cloneExtentCache(tracked.extentByCacheKey),
    lastCacheKey: tracked.lastCacheKey,
    hasSettledRender: tracked.hasSettledRender,
  });
}

function restoreCrackmapSessionCache(
  layerId: ActiveDefectLayerId,
  tracked: TrackedCrackmapLayer,
  sessionCache: ReadonlyMap<ActiveDefectLayerId, CrackmapSessionCache>,
): boolean {
  const cached = sessionCache.get(layerId);
  if (!cached) {
    return false;
  }

  tracked.featureCache = cloneFeatureCache(cached.featureCache);
  tracked.extentByCacheKey = cloneExtentCache(cached.extentByCacheKey);
  tracked.lastCacheKey = cached.lastCacheKey;
  tracked.hasSettledRender = cached.hasSettledRender;
  return true;
}

interface MapViewExtentLike {
  readonly xmin: number;
  readonly ymin: number;
  readonly xmax: number;
  readonly ymax: number;
}

function isMapViewExtentLike(value: unknown): value is MapViewExtentLike {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as MapViewExtentLike;
  return (
    Number.isFinite(candidate.xmin) &&
    Number.isFinite(candidate.ymin) &&
    Number.isFinite(candidate.xmax) &&
    Number.isFinite(candidate.ymax)
  );
}

function crackmapLayerSignature(layers: readonly ActiveDefectLayer[]): string {
  return layers
    .filter((layer) => isDefectOverlayLayerKind(layer.layerKind))
    .map(
      (layer) =>
        `${layer.id}:w${layer.sourceLayerId ?? ''}:u${layer.vectorSourceUrl ?? ''}:v${layer.visible ? '1' : '0'}:o${layer.opacity}:h${layer.highlighted ? '1' : '0'}:lw${layer.lineWidth}:ls${layer.lineStyle}:n${layer.displayName}`,
    )
    .join('|');
}

function getLayerLoadErrorMessage(layer: InstanceType<typeof GeoJSONLayer>): string {
  const loadError = layer.loadError as { message?: string } | null | undefined;
  return loadError?.message ?? 'Unknown layer load error';
}

interface LayerViewUpdatingLike {
  readonly updating: boolean;
  when: () => Promise<void>;
  watch: (property: string, callback: (value: boolean) => void) => { remove: () => void };
}

async function waitForLayerViewSettled(
  layerView: LayerViewUpdatingLike,
  timeoutMs = LAYER_VIEW_SETTLE_TIMEOUT_MS,
): Promise<void> {
  await layerView.when();
  if (!layerView.updating) {
    return;
  }

  await Promise.race([
    reactiveUtils.whenOnce(() => !layerView.updating),
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, timeoutMs);
    }),
  ]);
}

async function waitForLayerReady(
  mapView: View,
  layer: InstanceType<typeof GeoJSONLayer>,
  options?: { readonly waitForPaint?: boolean },
): Promise<void> {
  await layer.when();
  if (layer.loadStatus === 'failed') {
    throw new Error(getLayerLoadErrorMessage(layer));
  }
  if (typeof layer.load === 'function') {
    await layer.load();
  }
  if (layer.loadStatus === 'failed') {
    throw new Error(getLayerLoadErrorMessage(layer));
  }
  // Hidden layers do not need a paint settle — skip to avoid hangs.
  if (options?.waitForPaint === false) {
    return;
  }
  // Wait until the pending layer has drawn before swapping, otherwise removing
  // the previous layer flashes an empty map while the new one is still painting.
  // Cap the wait so a stuck layerView cannot freeze the replace queue.
  const layerView = await mapView.whenLayerView(layer);
  await waitForLayerViewSettled(layerView as LayerViewUpdatingLike);
}

function readMapViewZoom(mapView: View | null | undefined): number {
  const zoom = mapView && typeof mapView.zoom === 'number' ? mapView.zoom : Number.NaN;
  return Number.isFinite(zoom) ? zoom : CRACKMAP_INITIAL_ZOOM;
}

function applyCrackmapPresentationToLayer(
  layerMeta: ActiveDefectLayer,
  layer: InstanceType<typeof GeoJSONLayer>,
  mapZoom: number = CRACKMAP_INITIAL_ZOOM,
): void {
  layer.title = layerMeta.displayName;
  layer.visible = layerMeta.visible;
  layer.opacity = layerMeta.opacity;
  const profile = resolveDefectOverlayProfile(layerMeta.layerKind, {
    sourceLayerId: layerMeta.sourceLayerId,
  });
  if (profile) {
    layer.renderer = profile.buildRenderer(
      layerMeta.lineWidth,
      layerMeta.lineStyle,
      mapZoom,
    );
  }
}

function applyCrackmapPresentation(
  layerMeta: ActiveDefectLayer,
  tracked: TrackedCrackmapLayer,
  mapZoom: number = CRACKMAP_INITIAL_ZOOM,
): void {
  if (!tracked.layer) {
    return;
  }

  applyCrackmapPresentationToLayer(layerMeta, tracked.layer, mapZoom);
}

function syncPas2161PointSizesForZoom(
  mapView: View,
  tracked: ReadonlyMap<ActiveDefectLayerId, TrackedCrackmapLayer>,
  activeLayers: readonly ActiveDefectLayer[],
  zoom: number,
): void {
  const roundedZoom = roundSurveyWfsPointZoom(zoom);
  for (const layerMeta of activeLayers) {
    if (!isDefectOverlayLayerKind(layerMeta.layerKind) || !layerMeta.visible) {
      continue;
    }
    const profile = resolveDefectOverlayProfile(layerMeta.layerKind, {
      sourceLayerId: layerMeta.sourceLayerId,
    });
    if (!profile || profile.geometryKind !== 'point') {
      continue;
    }
    const entry = tracked.get(layerMeta.id);
    if (!entry?.layer) {
      continue;
    }
    entry.layer.renderer = profile.buildRenderer(
      layerMeta.lineWidth,
      layerMeta.lineStyle,
      roundedZoom,
    );
  }
}

function destroyMapLayer(layer: { destroy?: () => void } | null | undefined): void {
  if (layer && typeof layer.destroy === 'function') {
    layer.destroy();
  }
}

function removeCrackmapMapLayers(
  map: {
    remove: (layer: unknown) => void;
    findLayerById?: (id: string) => unknown;
    layers?: {
      toArray?: () => readonly unknown[];
      length?: number;
      getItemAt?: (index: number) => unknown;
    };
  },
  mapLayerId: string,
): void {
  const pendingPrefix = `${mapLayerId}__pending`;
  const matched: unknown[] = [];

  const byId = map.findLayerById?.(mapLayerId);
  if (byId) {
    matched.push(byId);
  }

  const collection = map.layers;
  if (collection && typeof collection.toArray === 'function') {
    matched.push(...collection.toArray());
  } else if (collection && typeof collection.getItemAt === 'function') {
    const length = typeof collection.length === 'number' ? collection.length : 0;
    for (let index = 0; index < length; index += 1) {
      matched.push(collection.getItemAt(index));
    }
  }

  const seen = new Set<unknown>();
  for (const layer of matched) {
    if (!layer || typeof layer !== 'object' || seen.has(layer)) {
      continue;
    }
    const id = (layer as { id?: unknown }).id;
    if (typeof id !== 'string') {
      continue;
    }
    if (id !== mapLayerId && !id.startsWith(pendingPrefix)) {
      continue;
    }
    seen.add(layer);
    try {
      map.remove(layer);
    } catch {
      // ignore remove races during hide
    }
    destroyMapLayer(layer as { destroy?: () => void });
  }
}

function detachCrackmapLayer(
  mapView: View | null,
  tracked: TrackedCrackmapLayer,
  mapLayerId?: string,
): void {
  tracked.refreshGeneration += 1;
  tracked.abortController?.abort();
  tracked.abortController = null;

  const layer = tracked.layer;
  tracked.layer = null;

  const resolvedMapLayerId =
    mapLayerId ??
    (layer && typeof (layer as { id?: unknown }).id === 'string'
      ? normalizeActiveDefectMapLayerId((layer as { id: string }).id) ?? undefined
      : undefined);

  if (mapView?.map && resolvedMapLayerId) {
    removeCrackmapMapLayers(mapView.map, resolvedMapLayerId);
  } else if (layer && mapView?.map) {
    try {
      mapView.map.remove(layer);
    } catch {
      // ignore remove races
    }
    destroyMapLayer(layer);
  } else {
    destroyMapLayer(layer);
  }

  if (tracked.blobUrl) {
    revokeGeoJsonLayerUrl(tracked.blobUrl);
    tracked.blobUrl = null;
  }
}

/** Keep the GeoJSONLayer mounted but hidden while overview WMTS is showing */
function hideCrackmapLayer(tracked: TrackedCrackmapLayer): void {
  if (tracked.layer) {
    tracked.layer.visible = false;
  }
}

function applyHighlightedLayerOrder(
  mapView: View,
  activeLayers: readonly ActiveDefectLayer[],
  tracked: Map<ActiveDefectLayerId, TrackedCrackmapLayer>,
): void {
  const map = mapView.map;
  if (!map || typeof map.reorder !== 'function') {
    return;
  }

  const nonHighlighted: InstanceType<typeof GeoJSONLayer>[] = [];
  const highlighted: InstanceType<typeof GeoJSONLayer>[] = [];

  for (const layerMeta of activeLayers) {
    if (!isDefectOverlayLayerKind(layerMeta.layerKind)) {
      continue;
    }
    const entry = tracked.get(layerMeta.id);
    if (!entry?.layer) {
      continue;
    }
    if (layerMeta.highlighted) {
      highlighted.push(entry.layer);
    } else {
      nonHighlighted.push(entry.layer);
    }
  }


  const ordered = [...nonHighlighted, ...highlighted];
  if (ordered.length === 0) {
    return;
  }

  const layersCollection = map.layers as {
    indexOf?: (layer: InstanceType<typeof GeoJSONLayer>) => number;
  };
  const indices = ordered
    .map((layer) =>
      typeof layersCollection.indexOf === 'function'
        ? layersCollection.indexOf(layer)
        : -1,
    )
    .filter((index) => index >= 0);
  if (indices.length === 0) {
    return;
  }

  // Keep the SDM/crackmap band at the absolute top so survey bring-to-top
  // (including WFS) cannot bury defects under imagery or feature layers.
  const groupTopIndex = Math.max(0, map.layers.length - 1);
  for (const layer of ordered) {
    map.reorder(layer, groupTopIndex);
  }
}


async function ensureCrackmapStreetLevelZoom(
  mapView: View,
  hasOverviewTiles: boolean,
): Promise<boolean> {
  if (isMapViewAtCrackmapStreetLevel(mapView)) {
    return true;
  }
  if (hasOverviewTiles) {
    return false;
  }
  if (typeof mapView.goTo !== 'function') {
    return false;
  }

  try {
    await mapView.goTo(
      { zoom: CRACKMAP_INITIAL_ZOOM },
      {
        duration: CRACKMAP_STREET_ZOOM_DURATION_MS,
        easing: 'ease-in-out',
        padding: getDashboardMapViewPadding(),
      },
    );
    return isMapViewAtCrackmapStreetLevel(mapView);
  } catch (error) {
    console.warn('Failed to zoom map for crackmap WFS fetch', error);
    return false;
  }
}

function readMapViewExtent(mapView: View): MapExtentBbox | null {
  const extent = mapView.extent;
  if (!isMapViewExtentLike(extent)) {
    return null;
  }
  return {
    xmin: extent.xmin,
    ymin: extent.ymin,
    xmax: extent.xmax,
    ymax: extent.ymax,
  };
}

/**
 * Street-scale bbox around the current map centre, prefetch
 * while overview tiles are still showing at zoom level 14
 */
function resolveStreetLevelPrefetchExtent(mapView: View): MapExtentBbox | null {
  const extent = readMapViewExtent(mapView);
  if (!extent) {
    return null;
  }
  const centerX = (extent.xmin + extent.xmax) / 2;
  const centerY = (extent.ymin + extent.ymax) / 2;
  if (!Number.isFinite(centerX) || !Number.isFinite(centerY)) {
    return null;
  }
  const widthPx =
    typeof mapView.width === 'number' && mapView.width > 0
      ? mapView.width
      : 1024;
  const heightPx =
    typeof mapView.height === 'number' && mapView.height > 0
      ? mapView.height
      : 768;
  return buildCrackmapStreetLevelPrefetchExtent(
    centerX,
    centerY,
    widthPx,
    heightPx,
    CRACKMAP_INITIAL_ZOOM,
  );
}

function isMapViewAtCrackmapStreetLevel(mapView: View): boolean {
  const zoom = typeof mapView.zoom === 'number' ? mapView.zoom : Number.NaN;
  return resolveCrackmapStreetLevelGate(zoom);
}

function ensureCrackmapLayerRevealed(
  layerMeta: ActiveDefectLayer,
  tracked: TrackedCrackmapLayer,
  mapZoom: number = CRACKMAP_INITIAL_ZOOM,
): void {
  if (!tracked.layer || !layerMeta.visible) {
    return;
  }
  applyCrackmapPresentation(layerMeta, tracked, mapZoom);
}

function isCrackmapLayerOnMap(
  mapView: View,
  tracked: TrackedCrackmapLayer,
): boolean {
  const map = mapView.map;
  return (
    tracked.layer !== null &&
    map !== null &&
    map.layers.includes(tracked.layer)
  );
}

function disposeTrackedLayer(
  mapView: View | null,
  layerId: ActiveDefectLayerId,
  tracked: TrackedCrackmapLayer,
  sessionCache: Map<ActiveDefectLayerId, CrackmapSessionCache>,
  mapLayerId?: string,
): void {
  persistCrackmapSessionCache(layerId, tracked, sessionCache);

  tracked.lastCacheKey = null;
  tracked.featureCache.clear();
  tracked.extentByCacheKey.clear();

  detachCrackmapLayer(
    mapView,
    tracked,
    mapLayerId ?? toActiveDefectMapLayerId(layerId),
  );
}

async function replaceCrackmapLayerFeatures(
  mapView: View,
  layerMeta: ActiveDefectLayer,
  tracked: TrackedCrackmapLayer,
  featureCollection: GeoJsonFeatureCollection,
  cacheKey: string,
  options?: {
    readonly mapVisible?: boolean;
    readonly isCurrent?: () => boolean;
  },
): Promise<void> {
  const mapVisible = options?.mapVisible ?? layerMeta.visible;
  const isCurrent = options?.isCurrent ?? (() => true);
  const mapLayerId = toActiveDefectMapLayerId(layerMeta.id);

  const discardPendingLayer = (
    map: { remove: (layer: unknown) => void },
    layer: InstanceType<typeof GeoJSONLayer>,
    blobUrl: string,
  ): void => {
    try {
      map.remove(layer);
    } catch {
      // ignore
    }
    destroyMapLayer(layer);
    revokeGeoJsonLayerUrl(blobUrl);
  };

  const runReplace = async (): Promise<void> => {
    if (!isCurrent()) {
      return;
    }
    if (!layerMeta.visible && mapVisible) {
      detachCrackmapLayer(mapView, tracked, mapLayerId);
      tracked.lastCacheKey = cacheKey;
      return;
    }

    const map = mapView.map;
    if (!map) {
      throw new Error('Map view has no map');
    }

    const mapZoom = readMapViewZoom(mapView);
    const profile = resolveDefectOverlayProfile(layerMeta.layerKind, {
      sourceLayerId: layerMeta.sourceLayerId,
    });
    const layerConfig = createCrackmapGeoJsonLayerConfig(
      mapLayerId,
      layerMeta.displayName,
      featureCollection,
      layerMeta.lineWidth,
      layerMeta.lineStyle,
      profile
        ? {
            geometryKind: profile.geometryKind,
            renderer: profile.buildRenderer(
              layerMeta.lineWidth,
              layerMeta.lineStyle,
              mapZoom,
            ),
          }
        : undefined,
    );

    const previousBlobUrl = tracked.blobUrl;

    if (tracked.layer) {
      const previousLayer = tracked.layer;
      const pendingLayerId = `${mapLayerId}__pending_${Date.now()}`;

      const nextLayer = new GeoJSONLayer({
        ...layerConfig.layerProperties,
        id: pendingLayerId,
      });
      // Keep the next layer visible while it paints under the previous one so
      // waitForLayerReady actually draws features before we remove the old bbox.
      applyCrackmapPresentationToLayer(layerMeta, nextLayer, mapZoom);
      if (!mapVisible) {
        nextLayer.visible = false;
      }

      map.add(nextLayer);
      const previousIndex =
        typeof map.layers?.indexOf === 'function' ? map.layers.indexOf(previousLayer) : -1;
      if (previousIndex >= 0 && typeof map.reorder === 'function') {
        map.reorder(nextLayer, previousIndex);
      }

      try {
        await waitForLayerReady(mapView, nextLayer, { waitForPaint: mapVisible });
      } catch (error) {
        discardPendingLayer(map, nextLayer, layerConfig.blobUrl);
        throw error;
      }

      // Hide/remove while painting: drop the pending swap; never reattach
      if (!isCurrent()) {
        discardPendingLayer(map, nextLayer, layerConfig.blobUrl);
        return;
      }

      // Previous extent stays on top until the next bbox has settled; then swap
      map.remove(previousLayer);
      destroyMapLayer(previousLayer);

      nextLayer.id = mapLayerId;
      applyCrackmapPresentationToLayer(layerMeta, nextLayer, mapZoom);
      if (!mapVisible) {
        nextLayer.visible = false;
      }

      tracked.layer = nextLayer;
      tracked.blobUrl = layerConfig.blobUrl;
      tracked.lastCacheKey = cacheKey;

      if (previousBlobUrl && previousBlobUrl !== tracked.blobUrl) {
        revokeGeoJsonLayerUrl(previousBlobUrl);
      }
      return;
    }

    removeCrackmapMapLayers(map, mapLayerId);

    const geoJsonLayer = new GeoJSONLayer(layerConfig.layerProperties);
    tracked.layer = geoJsonLayer;
    tracked.blobUrl = layerConfig.blobUrl;
    applyCrackmapPresentation(layerMeta, tracked, mapZoom);
    if (!mapVisible) {
      geoJsonLayer.visible = false;
    }
    map.add(geoJsonLayer);

    await waitForLayerReady(mapView, geoJsonLayer, { waitForPaint: mapVisible });

    if (!isCurrent()) {
      discardPendingLayer(map, geoJsonLayer, layerConfig.blobUrl);
      if (tracked.layer === geoJsonLayer) {
        tracked.layer = null;
        tracked.blobUrl = null;
      }
      return;
    }

    if (previousBlobUrl && previousBlobUrl !== tracked.blobUrl) {
      revokeGeoJsonLayerUrl(previousBlobUrl);
    }

    tracked.lastCacheKey = cacheKey;
    applyCrackmapPresentation(layerMeta, tracked, mapZoom);
    if (!mapVisible) {
      geoJsonLayer.visible = false;
    }
  };

  const replaceTask = tracked.replacePromise.then(runReplace, runReplace);
  tracked.replacePromise = replaceTask.catch(() => undefined);
  await replaceTask;
}

function layerHasFeatures(tracked: TrackedCrackmapLayer): boolean {
  return (
    tracked.layer !== null &&
    tracked.blobUrl !== null &&
    tracked.lastCacheKey !== null &&
    tracked.featureCache.has(tracked.lastCacheKey)
  );
}

function getDisplayedFeatureCollection(
  tracked: TrackedCrackmapLayer,
): GeoJsonFeatureCollection | null {
  if (!tracked.lastCacheKey) {
    return null;
  }
  return tracked.featureCache.get(tracked.lastCacheKey) ?? null;
}

function layerDisplaysFeatureCollection(
  tracked: TrackedCrackmapLayer,
  featureCollection: GeoJsonFeatureCollection,
): boolean {
  return layerHasFeatures(tracked) && getDisplayedFeatureCollection(tracked) === featureCollection;
}

async function refreshCrackmapLayerForExtent(
  mapView: View,
  layerMeta: ActiveDefectLayer,
  tracked: TrackedCrackmapLayer,
  extent: MapExtentBbox,
  isCurrent: () => boolean,
  loadingControls?: CrackmapFetchLoadingControls,
): Promise<void> {
  const wfsEndpoint =
    layerMeta.vectorSourceUrl?.trim() || layerMeta.sourceLayerId?.trim() || '';
  if (!wfsEndpoint || !layerMeta.visible) {
    return;
  }

  const profile = resolveDefectOverlayProfile(layerMeta.layerKind, {
    sourceLayerId: layerMeta.sourceLayerId,
  });
  if (!profile) {
    return;
  }

  const cacheKey = buildCrackmapBboxCacheKey(extent);

  const mapZoom = readMapViewZoom(mapView);

  if (
    tracked.lastCacheKey === cacheKey &&
    layerHasFeatures(tracked) &&
    isCrackmapLayerOnMap(mapView, tracked)
  ) {
    ensureCrackmapLayerRevealed(layerMeta, tracked, mapZoom);
    return;
  }

  const exactCachedFeatures = tracked.featureCache.get(cacheKey);
  if (exactCachedFeatures) {
    if (!isCurrent()) {
      return;
    }
    if (
      layerDisplaysFeatureCollection(tracked, exactCachedFeatures) &&
      isCrackmapLayerOnMap(mapView, tracked)
    ) {
      tracked.lastCacheKey = cacheKey;
      ensureCrackmapLayerRevealed(layerMeta, tracked, mapZoom);
      return;
    }
    await replaceCrackmapLayerFeatures(
      mapView,
      layerMeta,
      tracked,
      exactCachedFeatures,
      cacheKey,
      { isCurrent },
    );
    return;
  }

  const containingCache = findContainingCachedCrackmapFeatures(
    extent,
    tracked.extentByCacheKey,
    tracked.featureCache,
  );
  if (containingCache) {
    tracked.featureCache.set(cacheKey, containingCache.features);
    tracked.extentByCacheKey.set(cacheKey, containingCache.extent);
    if (!isCurrent()) {
      return;
    }
    if (
      isCrackmapLayerOnMap(mapView, tracked) &&
      layerDisplaysFeatureCollection(tracked, containingCache.features)
    ) {
      tracked.lastCacheKey = cacheKey;
      ensureCrackmapLayerRevealed(layerMeta, tracked, mapZoom);
      return;
    }
    await replaceCrackmapLayerFeatures(
      mapView,
      layerMeta,
      tracked,
      containingCache.features,
      cacheKey,
      { isCurrent },
    );
    return;
  }

  // Zoom out/in often yields a near-equal street bbox that is not a strict
  // child of the previous key — reuse the on-map layer instead of refetching.
  if (
    layerHasFeatures(tracked) &&
    isCrackmapLayerOnMap(mapView, tracked) &&
    tracked.lastCacheKey
  ) {
    const lastExtent = tracked.extentByCacheKey.get(tracked.lastCacheKey);
    if (lastExtent && isMapExtentApproximatelyEqual(extent, lastExtent)) {
      ensureCrackmapLayerRevealed(layerMeta, tracked, mapZoom);
      return;
    }
  }

  // Never issue a display WFS request for a zoomed-out / city-scale viewport.
  // Prefetch at z-level 14+ uses prefetchCrackmapFeaturesForExtent instead
  if (!isMapViewAtCrackmapStreetLevel(mapView)) {
    return;
  }

  const refreshGeneration = tracked.refreshGeneration + 1;
  tracked.refreshGeneration = refreshGeneration;

  const isStillCurrent = (): boolean =>
    isCurrent() && tracked.refreshGeneration === refreshGeneration;

  tracked.abortController?.abort();
  const abortController = new AbortController();
  tracked.abortController = abortController;

  const loadingMessage = formatDefectOverlayLoadingMessage(
    profile.loadingLabel,
  );
  // Show while fetching and keep until features have painted.
  const showFetchOverlay = shouldShowCrackmapMapLoadingOverlay({
    isFetching: true,
    hasDisplayedFeatures: layerHasFeatures(tracked),
  });
  if (showFetchOverlay) {
    loadingControls?.begin(loadingMessage);
  }
  let failed = false;

  try {
    const fetchResult = await fetchCrackmapWfsFeatures(
      wfsEndpoint,
      extent,
      abortController.signal,
      profile.propertyNames,
    );
    if (abortController.signal.aborted || !isStillCurrent()) {
      return;
    }

    const { collection: featureCollection, truncated, totalFeatures, returnedFeatures } =
      fetchResult;

    tracked.featureCache.set(cacheKey, featureCollection);
    tracked.extentByCacheKey.set(cacheKey, extent);

    if (!featureCollection.features.length) {
      console.warn('Crackmap WFS returned no features for extent', {
        layerId: layerMeta.id,
        wfsEndpoint,
        extent,
      });
    } else if (truncated) {
      console.warn('Crackmap WFS response was truncated for extent', {
        layerId: layerMeta.id,
        wfsEndpoint,
        extent,
        totalFeatures,
        returnedFeatures,
      });
    }

    if (!isStillCurrent()) {
      return;
    }

    await replaceCrackmapLayerFeatures(
      mapView,
      layerMeta,
      tracked,
      featureCollection,
      cacheKey,
      { isCurrent: isStillCurrent },
    );
    loadingControls?.clearError();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return;
    }
    if (!isStillCurrent()) {
      return;
    }
    failed = true;
    const profile = resolveDefectOverlayProfile(layerMeta.layerKind, {
    sourceLayerId: layerMeta.sourceLayerId,
  });
    console.error(
      'Failed to refresh crackmap WFS layer',
      layerMeta.id,
      getCrackmapWfsErrorMessage(error),
      error,
    );
    loadingControls?.fail(
      formatDefectOverlayLoadFailure(profile?.loadingLabel ?? 'overlay'),
    );
  } finally {
    if (showFetchOverlay && !failed) {
      loadingControls?.end();
    }
  }
}

/**
 * quietly warms the WFS feature cache for a street-scale footprint around the
 * map centre while overview tiles are still visible z-level 14. Does not paint
 * vectors or show the loading overlay, z18 refresh then hits cache
 */
async function prefetchCrackmapFeaturesForExtent(
  mapView: View,
  layerMeta: ActiveDefectLayer,
  tracked: TrackedCrackmapLayer,
  extent: MapExtentBbox,
  isCurrent: () => boolean,
): Promise<void> {
  const wfsEndpoint =
    layerMeta.vectorSourceUrl?.trim() || layerMeta.sourceLayerId?.trim() || '';
  if (!wfsEndpoint || !layerMeta.visible) {
    return;
  }

  const profile = resolveDefectOverlayProfile(layerMeta.layerKind, {
    sourceLayerId: layerMeta.sourceLayerId,
  });
  if (!profile) {
    return;
  }

  const cacheKey = buildCrackmapBboxCacheKey(extent);
  if (tracked.featureCache.has(cacheKey)) {
    return;
  }

  const containingCache = findContainingCachedCrackmapFeatures(
    extent,
    tracked.extentByCacheKey,
    tracked.featureCache,
  );
  if (containingCache) {
    tracked.featureCache.set(cacheKey, containingCache.features);
    tracked.extentByCacheKey.set(cacheKey, containingCache.extent);
    return;
  }

  const refreshGeneration = tracked.refreshGeneration + 1;
  tracked.refreshGeneration = refreshGeneration;

  const isStillCurrent = (): boolean =>
    isCurrent() && tracked.refreshGeneration === refreshGeneration;

  tracked.abortController?.abort();
  const abortController = new AbortController();
  tracked.abortController = abortController;

  try {
    const fetchResult = await fetchCrackmapWfsFeatures(
      wfsEndpoint,
      extent,
      abortController.signal,
      profile.propertyNames,
    );
    if (abortController.signal.aborted || !isStillCurrent()) {
      return;
    }

    tracked.featureCache.set(cacheKey, fetchResult.collection);
    tracked.extentByCacheKey.set(cacheKey, extent);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return;
    }
    if (!isStillCurrent()) {
      return;
    }
    // Prefetch failures stay quiet, street-level refresh will retry with UI
    console.warn(
      'Crackmap WFS prefetch failed',
      layerMeta.id,
      getCrackmapWfsErrorMessage(error),
    );
  }
}

export function useDefectOverlayVectorsOnMap(view: View | null): DefectOverlayVectorsMapLoadingState {
  const dispatch = useDispatch<AppDispatch>();
  const activeLayers = useSelector(selectActiveDefectLayers);
  const pendingMapLayerIds = useSelector(selectPendingMapLayerIds);
  const pendingMapLayerIdSet = useMemo(
    () => new Set(pendingMapLayerIds),
    [pendingMapLayerIds],
  );
  const { loadingState, controls: loadingControls } = useCrackmapFetchLoadingState();
  const loadingControlsRef = useRef(loadingControls);
  loadingControlsRef.current = loadingControls;
  const trackedRef = useRef<Map<ActiveDefectLayerId, TrackedCrackmapLayer>>(new Map());
  const sessionCacheRef = useRef<Map<ActiveDefectLayerId, CrackmapSessionCache>>(new Map());
  const loadingRef = useRef<Set<ActiveDefectLayerId>>(new Set());
  const activeLayersRef = useRef(activeLayers);
  activeLayersRef.current = activeLayers;

  // Dismiss sticky error when every crackmap is turned off in the survey viewer.
  useEffect(() => {
    const hasVisibleOverlay = activeLayers.some(
      (layer) => isDefectOverlayLayerKind(layer.layerKind) && layer.visible,
    );
    if (!hasVisibleOverlay) {
      loadingControlsRef.current.clearError();
    }
  }, [activeLayers]);

  useEffect(() => {
    if (!view?.map) {
      return;
    }

    const tracked = trackedRef.current;
    for (const layerMeta of activeLayers) {
      if (!isDefectOverlayLayerKind(layerMeta.layerKind)) {
        continue;
      }
      const entry = tracked.get(layerMeta.id);
      if (!entry) {
        continue;
      }

      if (!layerMeta.visible) {
        detachCrackmapLayer(view, entry, toActiveDefectMapLayerId(layerMeta.id));
        continue;
      }

      if (!entry.hasSettledRender || loadingRef.current.has(layerMeta.id)) {
        continue;
      }

      if (isCrackmapLayerOnMap(view, entry)) {
        applyCrackmapPresentation(layerMeta, entry, readMapViewZoom(view));
        continue;
      }

      const hasOverview = resolveDefectOverviewGrid(layerMeta.sourceLayerId) != null;
      void (async () => {
        const zoomedIn = await ensureCrackmapStreetLevelZoom(view, hasOverview);
        if (!zoomedIn) {
          return;
        }

        const extent = readMapViewExtent(view);
        if (!extent) {
          return;
        }

        await refreshCrackmapLayerForExtent(
          view,
          layerMeta,
          entry,
          extent,
          () =>
            trackedRef.current.get(layerMeta.id) === entry &&
            activeLayersRef.current.some(
              (layer) => layer.id === layerMeta.id && layer.visible,
            ),
          loadingControls,
        );
      })().catch((error) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        console.error(
          'Failed to restore crackmap WFS layer',
          layerMeta.id,
          getCrackmapWfsErrorMessage(error),
          error,
        );
        const profile = resolveDefectOverlayProfile(layerMeta.layerKind, {
          sourceLayerId: layerMeta.sourceLayerId,
        });
        loadingControls.fail(
          formatDefectOverlayLoadFailure(profile?.loadingLabel ?? 'overlay'),
        );
      });
    }
    applyHighlightedLayerOrder(view, activeLayers, tracked);
  }, [view, crackmapLayerSignature(activeLayers)]);

  useEffect(() => {
    if (!view?.map) {
      return;
    }

    const overlayLayers = activeLayers.filter((layer) =>
      isDefectOverlayLayerKind(layer.layerKind),
    );
    const activeIds = new Set<ActiveDefectLayerId>(overlayLayers.map((layer) => layer.id));
    const tracked = trackedRef.current;

    for (const [layerId, entry] of [...tracked.entries()]) {
      if (!activeIds.has(layerId)) {
        disposeTrackedLayer(
          view,
          layerId,
          entry,
          sessionCacheRef.current,
          toActiveDefectMapLayerId(layerId),
        );
        tracked.delete(layerId);
      }
    }

    for (const layerMeta of overlayLayers) {
      const layerId = layerMeta.id;
      if (tracked.has(layerId) || loadingRef.current.has(layerId)) {
        const entry = tracked.get(layerId);
        if (
          entry?.hasSettledRender &&
          pendingMapLayerIdSet.has(layerId) &&
          !loadingRef.current.has(layerId)
        ) {
          const hasOverview =
            resolveDefectOverviewGrid(layerMeta.sourceLayerId) != null;
          const atStreetLevel = isMapViewAtCrackmapStreetLevel(view);
          if (!hasOverview || atStreetLevel || isCrackmapLayerOnMap(view, entry)) {
            dispatch(layerMapRenderSettled(layerId));
          }
        }
        continue;
      }

      const sourceLayerId = layerMeta.sourceLayerId?.trim();
      const vectorSourceUrl = layerMeta.vectorSourceUrl?.trim();
      if (!vectorSourceUrl && !sourceLayerId) {
        const profile = resolveDefectOverlayProfile(layerMeta.layerKind, {
          sourceLayerId: layerMeta.sourceLayerId,
        });
        console.error('Overlay layer missing vectorSourceUrl/sourceLayerId', layerId);
        loadingControlsRef.current.fail(
          formatDefectOverlayLoadFailure(
            profile?.loadingLabel ?? 'overlay',
            'missing WFS configuration',
          ),
        );
        dispatch(layerMapRenderSettled(layerId));
        continue;
      }

      loadingRef.current.add(layerId);

      void (async () => {
        const settleMapRender = (): void => {
          dispatch(layerMapRenderSettled(layerId));
        };

        const trackedEntry: TrackedCrackmapLayer = {
          layer: null,
          blobUrl: null,
          featureCache: new Map(),
          extentByCacheKey: new Map(),
          lastCacheKey: null,
          abortController: null,
          refreshGeneration: 0,
          hasSettledRender: false,
          replacePromise: Promise.resolve(),
        };
        restoreCrackmapSessionCache(
          layerId,
          trackedEntry,
          sessionCacheRef.current,
        );
        trackedRef.current.set(layerId, trackedEntry);

        try {
          if (!view.map) {
            settleMapRender();
            return;
          }

          const currentMeta =
            activeLayersRef.current.find((layer) => layer.id === layerId) ?? layerMeta;

          const isCurrent = (): boolean =>
            trackedRef.current.get(layerId) === trackedEntry &&
            activeLayersRef.current.some(
              (layer) => layer.id === layerId && layer.visible,
            );

          const hasOverview =
            resolveDefectOverviewGrid(currentMeta.sourceLayerId) != null;
          const atStreetLevel = isMapViewAtCrackmapStreetLevel(view);

          if (!atStreetLevel && hasOverview) {
            // Overview WMTS covers lower zooms, defer painting until street level
            // Keep this layer in pendingMapLayerIds until the overview hook settles
            trackedEntry.hasSettledRender = true;
            // From z14, quietly warm a street-scale WFS cache around the centre
            if (isCrackmapWfsPrefetchZoom(readMapViewZoom(view))) {
              const prefetchExtent = resolveStreetLevelPrefetchExtent(view);
              if (prefetchExtent) {
                void prefetchCrackmapFeaturesForExtent(
                  view,
                  currentMeta,
                  trackedEntry,
                  prefetchExtent,
                  isCurrent,
                );
              }
            }
            return;
          }

          const zoomedIn = await ensureCrackmapStreetLevelZoom(view, hasOverview);
          if (!zoomedIn) {
            console.warn('Crackmap WFS fetch skipped: map is below street-level zoom', layerId);
            trackedEntry.hasSettledRender = true;
            settleMapRender();
            return;
          }

          const viewExtent = readMapViewExtent(view);
          if (!viewExtent) {
            console.warn('Crackmap WFS fetch skipped: map view has no extent', layerId);
            settleMapRender();
            return;
          }

          const latestMeta =
            activeLayersRef.current.find((layer) => layer.id === layerId) ??
            currentMeta;

          await refreshCrackmapLayerForExtent(
            view,
            latestMeta,
            trackedEntry,
            viewExtent,
            isCurrent,
            loadingControlsRef.current,
          );

          const stillActive = activeLayersRef.current.some((layer) => layer.id === layerId);
          if (!stillActive) {
            disposeTrackedLayer(
              view,
              layerId,
              trackedEntry,
              sessionCacheRef.current,
              toActiveDefectMapLayerId(layerId),
            );
            trackedRef.current.delete(layerId);
            settleMapRender();
            return;
          }

          trackedEntry.hasSettledRender = true;
          applyHighlightedLayerOrder(view, activeLayersRef.current, trackedRef.current);
          settleMapRender();
        } catch (error) {
          if (!(error instanceof DOMException && error.name === 'AbortError')) {
            console.error(
              'Failed to load crackmap WFS layer on map',
              layerId,
              getCrackmapWfsErrorMessage(error),
              error,
            );
            const profile = resolveDefectOverlayProfile(layerMeta.layerKind, {
              sourceLayerId: layerMeta.sourceLayerId,
            });
            loadingControlsRef.current.fail(
              formatDefectOverlayLoadFailure(
                profile?.loadingLabel ?? 'overlay',
              ),
            );
          }
          settleMapRender();
        } finally {
          loadingRef.current.delete(layerId);
        }
      })();
    }
  }, [view, crackmapLayerSignature(activeLayers), dispatch, pendingMapLayerIdSet]);

  useEffect(() => {
    if (!view?.map) {
      return;
    }

    let debounceTimer: number | null = null;

    const refreshVisibleCrackmaps = (): void => {
      if (!view.stationary) {
        return;
      }

      const tracked = trackedRef.current;
      const mapZoom = readMapViewZoom(view);
      const atStreetLevel = isMapViewAtCrackmapStreetLevel(view);

      if (!atStreetLevel) {
        loadingControlsRef.current.clearError();
        const allowPrefetch = isCrackmapWfsPrefetchZoom(mapZoom);
        const prefetchExtent = allowPrefetch
          ? resolveStreetLevelPrefetchExtent(view)
          : null;

        for (const layerMeta of activeLayersRef.current) {
          if (!isDefectOverlayLayerKind(layerMeta.layerKind)) {
            continue;
          }
          if (resolveDefectOverviewGrid(layerMeta.sourceLayerId) == null) {
            continue;
          }
          const entry = tracked.get(layerMeta.id);
          if (!entry) {
            continue;
          }
          if (isCrackmapLayerOnMap(view, entry)) {
            hideCrackmapLayer(entry);
          }
          if (!allowPrefetch || !prefetchExtent || !layerMeta.visible) {
            // Below prefetch zoom — cancel any in-flight WFS while overview shows.
            entry.abortController?.abort();
            entry.abortController = null;
            continue;
          }
          if (!entry.hasSettledRender || loadingRef.current.has(layerMeta.id)) {
            continue;
          }
          void prefetchCrackmapFeaturesForExtent(
            view,
            layerMeta,
            entry,
            prefetchExtent,
            () =>
              trackedRef.current.get(layerMeta.id) === entry &&
              activeLayersRef.current.some(
                (layer) => layer.id === layerMeta.id && layer.visible,
              ),
          ).catch((error) => {
            if (!(error instanceof DOMException && error.name === 'AbortError')) {
              console.warn(
                'Failed to prefetch crackmap WFS layer',
                layerMeta.id,
                error,
              );
            }
          });
        }
        return;
      }

      const extent = view.extent;
      if (!isMapViewExtentLike(extent)) {
        return;
      }

      for (const layerMeta of activeLayersRef.current) {
        if (
          !isDefectOverlayLayerKind(layerMeta.layerKind) ||
          !layerMeta.visible
        ) {
          continue;
        }
        const entry = tracked.get(layerMeta.id);
        if (!entry?.hasSettledRender || loadingRef.current.has(layerMeta.id)) {
          continue;
        }

        ensureCrackmapLayerRevealed(layerMeta, entry, mapZoom);

        void refreshCrackmapLayerForExtent(
          view,
          layerMeta,
          entry,
          extent,
          () =>
            trackedRef.current.get(layerMeta.id) === entry &&
            activeLayersRef.current.some(
              (layer) => layer.id === layerMeta.id && layer.visible,
            ),
          loadingControlsRef.current,
        ).catch((error) => {
          if (!(error instanceof DOMException && error.name === 'AbortError')) {
            console.error('Failed to refresh crackmap WFS layer', layerMeta.id, error);
          }
        });
      }
    };

    const scheduleRefresh = (): void => {
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer);
      }
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        refreshVisibleCrackmaps();
      }, EXTENT_DEBOUNCE_MS);
    };

    const stationaryHandle = reactiveUtils.watch(
      () => view.stationary as boolean,
      (isStationary: boolean) => {
        if (!isStationary) {
          // Pan / zoom interaction — dismiss sticky error immediately.
          loadingControlsRef.current.clearError();
          return;
        }
        scheduleRefresh();
      },
    );

    let prevPointZoom = roundSurveyWfsPointZoom(readMapViewZoom(view));
    const zoomHandle = reactiveUtils.watch(
      () => view.zoom as number,
      (zoom: number) => {
        const roundedZoom = roundSurveyWfsPointZoom(zoom);
        if (roundedZoom === prevPointZoom) {
          return;
        }
        prevPointZoom = roundedZoom;
        syncPas2161PointSizesForZoom(
          view,
          trackedRef.current,
          activeLayersRef.current,
          roundedZoom,
        );
      },
    );

    return () => {
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer);
      }
      stationaryHandle.remove();
      zoomHandle.remove();
    };
  }, [view]);

  useEffect(() => {
    const mapView = view;
    return () => {
      const tracked = trackedRef.current;
      for (const [layerId, entry] of tracked.entries()) {
        disposeTrackedLayer(
          mapView,
          layerId,
          entry,
          sessionCacheRef.current,
          toActiveDefectMapLayerId(layerId),
        );
      }
      tracked.clear();
      loadingRef.current.clear();
    };
  }, [view]);

  return loadingState;
}
